import { approveToteThunk } from '@redux/features/order.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    onApproveComplete?: () => void;
};

const ApproveToteModal: FC<IProps> = ({ show, handleClose, onApproveComplete }) => {
    const { approveToteLoading } = useAppSelector((state: RootState) => state.orderState);
    const dispatch = useAppDispatch();
    const [scannedCode, setScannedCode] = useState('');

    const validationSchema = Yup.object({
        barCode: Yup.string().required('Barcode is required').min(1, 'Barcode cannot be empty'),
    });

    const formik = useFormik({
        initialValues: {
            barCode: '',
        },
        validationSchema,
        onSubmit: (values) => {
            if (values.barCode && !approveToteLoading) {
                dispatch(approveToteThunk(values.barCode.toString())).then((res: any) => {
                    if (res.meta.requestStatus === 'fulfilled') {
                        formik.resetForm();
                        handleClose();
                        if (onApproveComplete) onApproveComplete();
                    }
                });
            }
        },
    });

    // 🧠 Auto scan mode logic
    useEffect(() => {
        if (!show) return; // لو المودال مش مفتوح بلاش نسمع الأحداث
        let buffer = '';
        let timer: ReturnType<typeof setTimeout>;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Enter') {
                if (buffer.length > 0) {
                    formik.setFieldValue('barCode', buffer);
                    setScannedCode(buffer);
                    buffer = '';
                }
            } else if (/^[a-zA-Z0-9]$/.test(e.key)) {
                buffer += e.key;
                clearTimeout(timer);
                timer = setTimeout(() => (buffer = ''), 100); // امسح بعد 100ms لو مفيش إدخال جديد
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [show]);

    useEffect(() => {
        if (!show) formik.resetForm();
    }, [show]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-6 shadow-xl rounded-lg border border-gray-200 overflow-scroll md:w-[550px] md:max-h-[85vh]"
            backdropClassName="backdrop-overlay fixed inset-0 z-[1051] bg-black/70 backdrop-blur-sm"
        >
            <Modal.Header className="border-b border-gray-200 pb-4 mb-4 flex justify-between items-center">
                <Modal.Title className="text-xl font-semibold text-gray-800">Delievered Tote</Modal.Title>
            </Modal.Header>

            <Modal.Body className="my-4">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3">Enter Barcode *</label>
                        <input
                            type="text"
                            id="barCode"
                            name="barCode"
                            value={formik.values.barCode}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            disabled={approveToteLoading}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all"
                            placeholder="Scan or enter barcode..."
                            autoFocus
                        />
                        {formik.touched.barCode && formik.errors.barCode && (
                            <p className="mt-2 text-sm text-red-600 font-medium">{String(formik.errors.barCode)}</p>
                        )}
                        {scannedCode && <p className="mt-2 text-xs text-green-600">Scanned: {scannedCode}</p>}
                    </div>
                </form>
            </Modal.Body>

            <Modal.Footer className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={approveToteLoading}
                />
                <ButtonBase
                    innerText={approveToteLoading ? 'Delieviring...' : 'Delievered'}
                    className="bg-yellow-600 hover:bg-yellow-700 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={approveToteLoading || !formik.values.barCode}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ApproveToteModal;
